import { useEffect, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { toast } from 'react-toastify';

import Loader from '@shared/components/Loader';
import AircraftOverview from '@shared/components/AircraftOverview';
import { AVINODE_DOCUMENTS_DISCLAIMER, toAircraftOverviewProps, toAircraftPhotos } from '@shared/utils/aircraftOverviewProps';
import { toDocuments } from '@shared/utils/operatorOverviewProps';
import { defaultErrorMessage } from '@/helpers';
import { fetchAircraft, fetchFleet, fetchFileLinks } from '@/helpers/mvMasterApi';

// Read-only: aircraft are still edited in Maverick, so there is no Edit action here.
const AircraftProfile = ({ toggleDialog, aircraftId }) => {
  const [ loading, setLoading ] = useState(false);
  const [ aircraft, setAircraft ] = useState(null);
  const [ assignedAircraft, setAssignedAircraft ] = useState([]);
  const [ photos, setPhotos ] = useState([]);
  const [ documents, setDocuments ] = useState([]);
  const [ memberAircraft, setMemberAircraft ] = useState(null);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      let record = null;
      try {
        record = await fetchAircraft(aircraftId);
        if (active) {
          setAircraft(record ?? null);
        }
      } catch (error) {
        if (active) {
          setAircraft(null);
          toast.error('Failed to load the aircraft. Please try again.', defaultErrorMessage);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
      if (!active || !record) {
        return;
      }
      // Optional sections fill in behind the overview rather than holding the loader.
      fetchFileLinks(record.images)
        .then((images) => active && setPhotos(toAircraftPhotos(images)))
        .catch(() => {});
      fetchFileLinks(record.files)
        .then((files) => active && setDocuments(files))
        .catch(() => active && toast.error('Failed to load documents. Please try again.', defaultErrorMessage));
      // A fleet aircraft belongs to its own fleet, so its fleet.id is the fleet to load.
      if (record.isFleet && record.fleet?.id) {
        fetchFleet(record.fleet.id)
          .then((detail) => active && setAssignedAircraft(detail?.aircraft ?? []))
          .catch(() => active && toast.error('Failed to load assigned aircraft. Please try again.', defaultErrorMessage));
      }
    })();

    return () => {
      active = false;
    };
  }, [ aircraftId ]);

  return (
    <Dialog
      title="AIRCRAFT OVERVIEW"
      onClose={toggleDialog}
      className="add-contact-details-model right-open-modal w-650px"
    >
      {loading && <Loader />}
      {aircraft && (
        <AircraftOverview
          {...toAircraftOverviewProps(aircraft, assignedAircraft)}
          photos={photos}
          documents={toDocuments(documents)}
          documentsDisclaimer={AVINODE_DOCUMENTS_DISCLAIMER}
          onAircraftClick={(member) => setMemberAircraft({ id: member.id })}
        />
      )}
      <div className="model-footer-action">
        <Button
          className="height-42 mob-width-full btn-large-padding border-0 fs-14 font-600"
          themeColor="primary"
          onClick={toggleDialog}
        >
          Close Window
        </Button>
      </div>
      {memberAircraft && (
        <AircraftProfile
          toggleDialog={() => setMemberAircraft(null)}
          aircraftId={memberAircraft.id}
        />
      )}
    </Dialog>
  );
};

export default AircraftProfile;
